import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, Check, ChevronRight, ClipboardCheck, Plus, X, Pencil, Trash2, Eye } from 'lucide-react';
import { z } from 'zod';
import { createCaminhao, getCaminhao, getCaminhaoById, updateCaminhao, deleteCaminhao, normalizeCaminhaoRegistro } from '../services/caminhoesService';
import { getConcretagemRealizada } from '../services/concretagemRealizadaService';
import { getObras } from '../services/obraService';
import { getEmpresa } from '../services/empresaService';
import { getEtapaObraEmpresa } from '../services/etapaObraService';
import { getTipoCimento } from '../services/tipoCimentoService';
import { getTipoConcreto } from '../services/tipoConcretoService';
import { getConcreteira } from '../services/concreteiraService';
import { getCartaTracoObra } from '../services/cartaTracoService';
import { getTipoCorpoProva } from '../services/tipoCorpoProvaService';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { normalizeDateOnly, formatDateBr } from '../utils/formatters';



const quantidadeCpOptions = Array.from({ length: 20 }, (_, i) => i + 1);
const quantidadeSlumpOptions = Array.from({ length: 20 }, (_, i) => i + 1);
const INITIAL_FILTERS = { empresa: '', obra: '', data: '' };

// Rótulos da flag de adição de água (0 = Não informado). Deve espelhar o enum do backend.
const ADICAO_AGUA_LABELS = {
  '0': 'Não informado',
  '1': 'Sem adição',
  '2': 'Adicionado sem informação da folga',
  '3': 'Adicionado dentro da folga',
  '4': 'Adicionado fora da folga',
};

const INITIAL_FORM_DATA = {
  concretagemRealizadaId: '', empresa: '', obra: '', dataConcretagem: '', placaCaminhao: '',
  etapaObra: '', tipoCimento: '', tipoConcreto: '', concreteira: '', cartaTraco: '',
  localConcretagem: '', valrClasResiCompressao: '', valrClasResiTracaoDireta: '',
  valrClasResiTracaoFlexao: '', valrClasResiTracaoIndireta: '', valrClasTrabalhabiilidade: '',
  numrNotaFiscalPrincipal: '', numrNotaFiscalSecundaria: '', valrVolume: '',
  flagSemNotaFiscal: '0',
  flagContAdicaoAgua: '0', flagIev: '0', dataSaidaUsina: '', dataChegadaUsina: '',
  dataChegadaObra: '', dataInicioDescarga: '', dataFimDescarga: '', valrTemperaturaConcreto: '',
  valrTemperaturaAmbiente: '',
  quantidadeSlump: '0',
  observacoes: '', quantidadeCp: '0',
};

let caminhoesDraftCache = null;

const schema = z.object({
  concretagemRealizadaId: z.string().min(1, 'Selecione uma concretagem.'),
  empresa: z.string().min(1, 'Empresa obrigatória.'),
  obra: z.string().min(1, 'Obra obrigatória.'),
  dataConcretagem: z.string().min(1, 'Data obrigatória.'),
  etapaObra: z.string().min(1, 'Etapa da obra obrigatória.'),
  tipoCimento: z.string().min(1, 'Tipo de cimento obrigatório.'),
  tipoConcreto: z.string().min(1, 'Tipo de concreto obrigatório.'),
  concreteira: z.string().min(1, 'Concreteira obrigatória.'),
  quantidadeCp: z.number({ invalid_type_error: 'Quantidade de CP obrigatória.' }).min(1, 'Quantidade de CP obrigatória.'),
});

// Classe Tailwind do badge de status (concluído vs. pendente).
function getStatusClass(status) {
  return status === 'Concluido' ? 'bg-[#0e7490]/10 text-[#0e7490]' : 'bg-amber-100 text-amber-700';
}

// Extrai HH:MM de um valor com horário explícito; retorna '--:--' se não houver.
function formatHour(value) {
  if (!value) return '--:--';
  const asString = String(value).trim();
  const hasExplicitTime = /[T\s]\d{2}:\d{2}/.test(asString) || /^\d{10,13}$/.test(asString);
  if (!hasExplicitTime) return '--:--';

  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return '--:--';
  const pad = (num) => String(num).padStart(2, '0');
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

// Converte um horário "HH:MM" em minutos do dia; retorna null se vazio/inválido ('--:--').
function parseHoraToMinutes(value) {
  const match = String(value || '').match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

// Normaliza para YYYY-MM-DDTHH:MM; retorna '' quando não há horário explícito.
function normalizeDateTime(value) {
  if (!value) return '';
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(asString)) return asString;
  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return '';

  const hasExplicitTime = /[T\s]\d{2}:\d{2}/.test(asString) || /^\d{10,13}$/.test(asString);
  if (!hasExplicitTime) return '';

  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

// Exibe data e hora no padrão brasileiro ("dd/mm/aaaa às HH:MM"); usa fallbackDate se faltar horário.
function formatDateTimeBr(value, fallbackDate) {
  if (!value) return formatDateBr(fallbackDate);
  const asString = String(value).trim();

  if (/^\d{2}\/\d{2}\/\d{4}/.test(asString)) return asString;

  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) {
    const [year, month, day] = asString.split('-');
    return `${day}/${month}/${year}`;
  }

  const hasExplicitTime = /[T\s]\d{2}:\d{2}/.test(asString) || /^\d{10,13}$/.test(asString);
  if (!hasExplicitTime) {
    return formatDateBr(asString);
  }

  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return asString;

  const dateStr = parsed.toLocaleDateString('pt-BR');
  const pad = (num) => String(num).padStart(2, '0');
  const timeStr = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;

  return `${dateStr} ${timeStr}`;
}

// Calcula a data de ruptura somando `idade` (dias) à data base, devolvendo YYYY-MM-DD.
function calculateDataRuptura(dataBase, idade) {
  if (!dataBase) return '';
  const base = normalizeDateOnly(dataBase);
  if (!base) return '';

  const parsedIdade = parseInt(idade, 10);
  if (isNaN(parsedIdade)) return base;

  const [year, month, day] = base.split('-');
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + parsedIdade);

  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Retorna o primeiro valor não-vazio entre `keys` do objeto, como string (resolve aliases da API).
function resolveFirstString(item, keys) {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
}

// AJUSTE BACKEND AQUI: quando a API mudar nomes de campos,
// ajuste idKeys/labelKeys usados em loadClassificacaoOptions.
function normalizeSelectOptions(data, idKeys, labelKeys, idPrefix) {
  if (!Array.isArray(data)) return [];

  const mapped = data.map((item, index) => {
    const label = resolveFirstString(item, labelKeys);
    const id = resolveFirstString(item, idKeys) || label || `${idPrefix}-${index}`;
    return { id, label: label || id };
  });

  const uniqueById = new Map();
  mapped.forEach((item) => {
    if (!uniqueById.has(item.id)) uniqueById.set(item.id, item);
  });

  return Array.from(uniqueById.values()).sort((a, b) => a.label.localeCompare(b.label));
}

// Normaliza uma obra da API para { id, nome, idEmpresa }.
function normalizeObra(item) {
  // AJUSTE BACKEND AQUI: mapeamento de chaves da API de obras.
  return {
    id: String(item.id || item.id_obra || item.idObra || ''),
    nome: item.nome_obra || item.nomeObra || item.nome || item.descricao || '-',
    idEmpresa: String(item.id_empresa || item.idEmpresa || item.empresa?.idEmpresa || item.empresa?.id_empresa || ''),
  };
}

// Normaliza uma empresa da API para { idEmpresa, razaoSocial }.
function normalizeEmpresa(item) {
  // AJUSTE BACKEND AQUI: mapeamento de chaves da API de empresas.
  return {
    idEmpresa: String(item.id_empresa || item.idEmpresa || item.id || ''),
    razaoSocial: item.razao_social || item.razaoSocial || item.nome || item.nome_fantasia || '-',
  };
}

// Monta a opção de concretagem do wizard, cruzando obra/empresa pelos índices e resolvendo data/hora.
function normalizeConcretagemOption(item, obraMetaById, empresaById) {
  // AJUSTE BACKEND AQUI: padroniza retorno da API de concretagem para o formato usado no wizard.
  const obraId = String(item.id_obra || item.idObra || item.obra?.id_obra || item.obra?.idObra || '');
  const obraMeta = obraMetaById[obraId];

  const itemEmpresa = item.empresa || item.obra?.empresa;
  const itemEmpresaStr = typeof itemEmpresa === 'object'
    ? (itemEmpresa?.razaoSocial || itemEmpresa?.nome || itemEmpresa?.nome_fantasia || '-')
    : (itemEmpresa || '-');

  const empresaNome = empresaById[String(obraMeta?.idEmpresa || '')] || itemEmpresaStr;
  const dataConcretagemRaw =
    item.data_hora_concretagem || item.dataHoraConcretagem || item.data_concretagem || item.dataConcretagem || item.data || '';
  const dataHoraConcretagem = normalizeDateTime(dataConcretagemRaw);

  return {
    id: String(item.id || item.idConcretagemRealizada || `conc-${Date.now()}-${Math.random()}`),
    idObra: obraId,
    idEmpresa: String(obraMeta?.idEmpresa || item.empresa?.idEmpresa || item.obra?.empresa?.idEmpresa || ''),
    obra: obraMeta?.nome || item.nome_obra ||
      (typeof item.obra === 'object' ? (item.obra?.nomeObra || item.obra?.nome || '-') : item.obra) || '-',
    empresa: empresaNome,
    data: normalizeDateOnly(dataConcretagemRaw),
    dataHoraConcretagem,
    // Quantidade de caminhões prevista no cadastro da concretagem (limite de cadastro).
    quantidadeCaminhao: Number(
      item.quantidadeCaminhao || item.quantidade_caminhoes || item.qtdCaminhoes || 0
    ),
  };
}

// Normaliza um número de nota fiscal para comparação de duplicidade: '', '0' e nulos viram '' (sem NF).
function normalizeNotaFiscalValue(value) {
  const asString = String(value ?? '').trim();
  return asString === '' || asString === '0' ? '' : asString;
}

function extractCreatedCaminhaoId(response) {
  // AJUSTE BACKEND AQUI: inclua aqui novos caminhos se o backend mudar o retorno do POST /api/caminhao.
  const candidates = [
    response?.id_caminhao,
    response?.idCaminhao,
    response?.id,
    response?.insertId,
    response?.data?.id_caminhao,
    response?.data?.idCaminhao,
    response?.data?.id,
    response?.caminhao?.id_caminhao,
    response?.caminhao?.id,
  ];

  const found = candidates.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
  return found ? String(found) : null;
}

/*function hasSlumpData(rows) {
  return Array.isArray(rows) && rows.length > 0;
}*/

// Tela de Caminhões: lista os registros e abre um wizard multi-etapas para cadastrar/editar
// caminhão de uma concretagem, com testes de slump e corpos de prova (CP).
function CaminhoesPage() {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingConcretagens, setLoadingConcretagens] = useState(true);
  const [query, setQuery] = useState('');
  const [isWizardOpen, setIsWizardOpen] = useState(() => caminhoesDraftCache?.isWizardOpen ?? false);
  const [editingCaminhaoId, setEditingCaminhaoId] = useState(null);
  const [filters, setFilters] = useState(() => caminhoesDraftCache?.filters ?? INITIAL_FILTERS);
  const [selectedConcretagem, setSelectedConcretagem] = useState(() => caminhoesDraftCache?.selectedConcretagem ?? null);
  const [currentStep, setCurrentStep] = useState(() => caminhoesDraftCache?.currentStep ?? 2);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false); // guarda sincrona contra cliques duplicados no mesmo tick
  const [saveMessage, setSaveMessage] = useState('');
  const [viewData, setViewData] = useState(null);
  const [formData, setFormData] = useState(() => caminhoesDraftCache?.formData ?? INITIAL_FORM_DATA);
  const [cpRows, setCpRows] = useState(() => caminhoesDraftCache?.cpRows ?? []);
  // Quando ligada, replica o tipo do primeiro CP para todos os demais.
  const [aplicarTipoTodosCp, setAplicarTipoTodosCp] = useState(false);
  const [cpSuggestions, setCpSuggestions] = useState(() => caminhoesDraftCache?.cpSuggestions ?? []);
  const cpEtiquetaRefs = useRef([]);

  useEffect(() => {
    setCpSuggestions((prev) => {
      const needed = cpRows.length;
      const base = Array.isArray(prev) ? prev.slice(0, needed) : [];
      while (base.length < needed) base.push('');
      return base;
    });
  }, [cpRows.length]);
  const [slumpRows, setSlumpRows] = useState(() => caminhoesDraftCache?.slumpRows ?? []);
  const [concretagensDisponiveis, setConcretagensDisponiveis] = useState([]);
  const [loadingClassificacao, setLoadingClassificacao] = useState(true);
  const [obrasList, setObrasList] = useState([]);
  const [etapasObraOptions, setEtapasObraOptions] = useState([]);
  const [tipoCimentoOptions, setTipoCimentoOptions] = useState([]);
  const [tipoConcretoOptions, setTipoConcretoOptions] = useState([]);
  const [concreteiraOptions, setConcreteiraOptions] = useState([]);
  const [cartaTracoOptions, setCartaTracoOptions] = useState([]);
  const [tipoCorpoProvaOptions, setTipoCorpoProvaOptions] = useState([]);
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    caminhoesDraftCache = { isWizardOpen, filters, selectedConcretagem, currentStep, formData, cpRows, cpSuggestions, slumpRows };
  }, [isWizardOpen, filters, selectedConcretagem, currentStep, formData, cpRows, cpSuggestions, slumpRows]);

  useEffect(() => {
    const qty = Number(formData.quantidadeSlump);
    if (formData.quantidadeSlump === '') {
      setSlumpRows([]);
      return;
    }

    if (!qty || qty < 0) {
      setSlumpRows([]);
      return;
    }

    setSlumpRows((prev) => {
      const next = [...prev];
      if (next.length > qty) return next.slice(0, qty);
      while (next.length < qty) {
        next.push({
          valrAdicaoAgua: '',
          valrAdicaoAditivo: '',
          valrSlump: '',
        });
      }
      return next;
    });
  }, [formData.quantidadeSlump]);

  useEffect(() => {
    const qty = Number(formData.quantidadeCp);
    if (formData.quantidadeCp === '') return;

    if (!qty || qty < 0) {
      setCpRows([]);
      return;
    }

    setCpRows((prev) => {
      const next = [...prev];
      if (next.length > qty) return next.slice(0, qty);
      // Novos CPs herdam o tipo do primeiro quando a flag esta ligada.
      const tipoPadrao = aplicarTipoTodosCp ? (next[0]?.tipo || '') : '';
      while (next.length < qty) next.push({ idadeRuptura: '', etiqueta: '', tipo: tipoPadrao });
      return next;
    });
  }, [formData.quantidadeCp, aplicarTipoTodosCp]);

  useEffect(() => {
    let isMounted = true;

    const loadRegistros = async () => {
      try {
        const data = await getCaminhao();
        if (!isMounted) return;

        if (Array.isArray(data)) {
          const normalized = data.map(normalizeCaminhaoRegistro).filter((item) => item.id);
          setRegistros(normalized.length > 0 ? normalized : []);
        }
      } catch (error) {
        if (isMounted) {
          setRegistros([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadRegistros();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadEtapaObra = async () => {
      if (!selectedConcretagem?.idEmpresa) {
        setEtapasObraOptions([]);
        return;
      }
      try {
        const result = await getEtapaObraEmpresa(selectedConcretagem.idEmpresa);
        if (!isMounted) return;
        setEtapasObraOptions(normalizeSelectOptions(
          result || [],
          ['id_etapa_obra', 'idEtapaObra', 'id'],
          ['nome_etapa_obra', 'nomeEtapaObra', 'descricao', 'nome'],
          'etp'
        ));
      } catch (error) {
        if (isMounted) setEtapasObraOptions([]);
      }
    };

    loadEtapaObra();

    /* EXCLUIR APÓS VALIDAÇÃO
    const loadTipoConcreto = async () => {
      if (!selectedConcretagem?.idEmpresa) {
        setTipoConcretoOptions([]);
        return;
      }
      try {
        const result = await getTipoConcretoEmpresa(selectedConcretagem.idEmpresa);
        if (!isMounted) return;
        setTipoConcretoOptions(normalizeSelectOptions(
          result || [],
          ['id_tipo_concreto', 'idTipoConcreto', 'id'],
          ['nome_tipo_concreto', 'nomeTipoConcreto', 'descricao', 'nome'],
          'tco'
        ));
      } catch (error) {
        if (isMounted) setTipoConcretoOptions([]);
      }
    };

    loadTipoConcreto();
    */

    const loadCartaTraco = async () => {
      if (!selectedConcretagem?.idObra) {
        setCartaTracoOptions([]);
        return;
      }
      try {
        const result = await getCartaTracoObra(selectedConcretagem.idObra);
        if (!isMounted) return;

        // Verifica se é array, caso a API retorne objeto único ou formatado incorretamente.
        const resultArray = Array.isArray(result) ? result : [result].filter(Boolean);

        setCartaTracoOptions(normalizeSelectOptions(
          resultArray,
          ['id_carta_traco', 'idCartaTraco', 'id'],
          ['nome_carta_traco', 'nomeCartaTraco', 'codgInternoDescricao', 'nome'],
          'ctr'
        ));
      } catch (error) {
        if (isMounted) setCartaTracoOptions([]);
      }
    };

    loadCartaTraco();

    return () => { isMounted = false; };
  }, [selectedConcretagem?.idEmpresa, selectedConcretagem?.idObra]);

  useEffect(() => {
    let isMounted = true;

    const loadClassificacaoOptions = async () => {
      // AJUSTE BACKEND AQUI: endpoints de catalogo usados nos selects do passo de classificacao.
      const [
        tipoCimentoResult,
        concreteiraResult,
        tipoCorpoProvaResult,
        tipoConcretoResult
      ] = await Promise.allSettled([
        getTipoCimento(),
        getConcreteira(),
        getTipoCorpoProva(),
        getTipoConcreto()
      ]);

      if (!isMounted) return;;

      // AJUSTE BACKEND AQUI (Tipo de cimento): idKeys e labelKeys controlam valor e texto do select.
      setTipoCimentoOptions(normalizeSelectOptions(
        tipoCimentoResult.status === 'fulfilled' ? tipoCimentoResult.value : [],
        ['id_tipo_cimento', 'idTipoCimento', 'id'],
        ['nome_tipo_cimento', 'nomeTipoCimento', 'descricao', 'nome', 'sigla'],
        'cim'
      ));

      // AJUSTE BACKEND AQUI (Concreteira): se backend renomear campos, ajuste somente estas listas.
      setConcreteiraOptions(normalizeSelectOptions(
        concreteiraResult.status === 'fulfilled' ? concreteiraResult.value : [],
        ['id_concreteira', 'idConcreteira', 'id'],
        ['nome_concreteira', 'nomeConcreteira', 'nome'],
        ['id_empresa', 'idEmpresa', 'id'],
        'con'
      ));

      // AJUSTE BACKEND AQUI (Tipo do corpo de prova): altere as chaves se o backend mudar o contrato.
      setTipoCorpoProvaOptions(normalizeSelectOptions(
        tipoCorpoProvaResult.status === 'fulfilled' ? tipoCorpoProvaResult.value : [],
        ['id_tipo_corpo_prova', 'idTipoCorpoProva', 'id'],
        ['nome_tipo_corpo_prova', 'nomeTipoCorpoProva', 'descricao', 'nome', 'sigla'],
        'tcp'
      ));

      // AJUSTE BACKEND AQUI (Tipo de concreto): altere as chaves se o backend mudar o contrato.
      setTipoConcretoOptions(normalizeSelectOptions(
        tipoConcretoResult.status === 'fulfilled' ? tipoConcretoResult.value : [],
        ['id_tipo_concreto', 'idTipoConcreto', 'id'],
        ['nome_tipo_concreto', 'nomeTipoConcreto', 'descricao', 'nome'],
        'tco'
      ));

      setLoadingClassificacao(false);
    };

    loadClassificacaoOptions().catch(() => {
      if (isMounted) setLoadingClassificacao(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadConcretagens = async () => {
      // AJUSTE BACKEND AQUI: dependencias para montar concretagem + obra + empresa no seletor inicial.
      const [concretagensResult, obrasResult, empresasResult] = await Promise.allSettled([
        getConcretagemRealizada(),
        getObras(),
        getEmpresa(),
      ]);

      if (!isMounted) return;

      const obras = obrasResult.status === 'fulfilled' && Array.isArray(obrasResult.value)
        ? obrasResult.value.map(normalizeObra).filter((item) => item.id)
        : [];

      setObrasList(obras);

      const empresas = empresasResult.status === 'fulfilled' && Array.isArray(empresasResult.value)
        ? empresasResult.value.map(normalizeEmpresa).filter((item) => item.idEmpresa)
        : [];

      setEmpresaOptions(empresas);

      const obraMetaById = obras.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});

      const empresaById = empresas.reduce((acc, item) => {
        acc[item.idEmpresa] = item.razaoSocial;
        return acc;
      }, {});

      if (concretagensResult.status === 'fulfilled' && Array.isArray(concretagensResult.value)) {
        const normalized = concretagensResult.value
          .map((item) => normalizeConcretagemOption(item, obraMetaById, empresaById))
          .filter((item) => item.id && item.data)
          .sort((a, b) => new Date(b.data) - new Date(a.data));

        if (normalized.length > 0) {
          setConcretagensDisponiveis(normalized);
        }
      }

      setLoadingConcretagens(false);
    };

    loadConcretagens().catch(() => {
      if (isMounted) setLoadingConcretagens(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedConcretagem) return;

    const updated = concretagensDisponiveis.find((item) => String(item.id) === String(selectedConcretagem.id));
    if (!updated) {
      setSelectedConcretagem(null);
      setFormData((prev) => ({
        ...prev,
        concretagemRealizadaId: '',
        empresa: '',
        obra: '',
        dataConcretagem: '',
      }));
      return;
    }

    setSelectedConcretagem(updated);
    setFormData((prev) => ({
      ...prev,
      concretagemRealizadaId: updated.id,
      empresa: updated.empresa,
      obra: updated.obra,
      dataConcretagem: updated.data,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concretagensDisponiveis]);

  const obraOptions = useMemo(() => {
    let filtered = obrasList;
    if (filters.empresa) {
      const selectedEmpresa = empresaOptions.find((e) => e.razaoSocial === filters.empresa);
      if (selectedEmpresa) {
        filtered = filtered.filter((obra) => String(obra.idEmpresa) === String(selectedEmpresa.idEmpresa));
      }
    }
    return Array.from(new Set(filtered.map((item) => item.nome).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [obrasList, filters.empresa, empresaOptions]);

  const concretagemById = useMemo(() => {
    return concretagensDisponiveis.reduce((acc, item) => {
      acc[String(item.id)] = item;
      return acc;
    }, {});
  }, [concretagensDisponiveis]);

  // Enriquece a listagem de caminhões com dados da concretagem quando o backend retornar apenas IDs.
  const registrosComConcretagem = useMemo(() => {
    return registros.map((item) => {
      const concretagem = concretagemById[String(item.idConcretagem || '')];
      return {
        ...item,
        obra: item.obra || concretagem?.obra || '-',
        empresa: item.empresa || concretagem?.empresa || '-',
        dataConcretagem: item.dataConcretagem || concretagem?.dataHoraConcretagem || concretagem?.data || '-',
      };
    });
  }, [registros, concretagemById]);

  const filteredConcretagens = useMemo(() => {
    if (!filters.empresa || !filters.obra) return [];

    return concretagensDisponiveis
      .filter((item) => (filters.empresa ? item.empresa === filters.empresa : true))
      .filter((item) => (filters.obra ? item.obra === filters.obra : true))
      .filter((item) => (filters.data ? item.data === filters.data : true))
      .sort((a, b) => new Date(b.data) - new Date(a.data));
  }, [filters, concretagensDisponiveis]);

  // A concretagem selecionada só filtra a tabela enquanto o card dela está visível na lista.
  const concretagemFiltroId = selectedConcretagem && filteredConcretagens.some((item) => String(item.id) === String(selectedConcretagem.id))
    ? String(selectedConcretagem.id)
    : '';

  const filteredRegistros = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return registrosComConcretagem
      .filter((item) => (filters.empresa ? item.empresa === filters.empresa : true))
      .filter((item) => (filters.obra ? item.obra === filters.obra : true))
      .filter((item) => (filters.data ? (item.dataConcretagem?.startsWith(filters.data) || item.dataConcretagem === filters.data) : true))
      .filter((item) => (concretagemFiltroId ? String(item.idConcretagem) === concretagemFiltroId : true))
      .filter((item) => {
        if (!normalized) return true;

        // Busca geral nos campos exibidos na lista de caminhões.
        const searchableText = [
          item.obra || '',
          item.empresa || '',
          item.notaFiscal || '',
          item.dataConcretagem || '',
          formatDateBr(item.dataConcretagem),
          formatDateTimeBr(item.dataConcretagem),
          String(item.quantidadeCp ?? ''),
          item.status || '',
        ]
          .join(' ')
          .toLowerCase();

        return searchableText.includes(normalized);
      })
      // Data decrescente (mais recentes primeiro) e, dentro da mesma data, nota fiscal crescente.
      .sort((a, b) => {
        const dataDiff = new Date(b.dataConcretagem) - new Date(a.dataConcretagem);
        if (dataDiff !== 0) return dataDiff;

        const nfA = Number(a.notaFiscal);
        const nfB = Number(b.notaFiscal);
        if (Number.isFinite(nfA) && Number.isFinite(nfB)) return nfA - nfB;
        return String(a.notaFiscal || '').localeCompare(String(b.notaFiscal || ''));
      });
  }, [filters, query, registrosComConcretagem, concretagemFiltroId]);

  const stats = useMemo(() => {
    const total = registrosComConcretagem.length;
    const concluidos = registrosComConcretagem.filter((item) => item.status === 'Concluido').length;
    const totalCps = registrosComConcretagem.reduce((acc, item) => acc + (Number(item.quantidadeCp) || 0), 0);
    return { total, concluidos, totalCps };
  }, [registrosComConcretagem]);

  // Handler de onChange dos filtros da listagem (empresa/obra/data).
  const handleFilterChange = (field) => (event) => setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  // Handler de onChange por campo do formulário (limpa o erro da regra de nota fiscal ao editar as notas).
  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));

    if (field === 'numrNotaFiscalPrincipal' || field === 'numrNotaFiscalSecundaria') {
      setErrors((prev) => {
        if (!prev.notaFiscalRegra) return prev;
        const { notaFiscalRegra: _removed, ...rest } = prev;
        return rest;
      });
    }

    // Ao editar/escolher um campo, limpa o erro de obrigatoriedade dele.
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const isSemNotaFiscal = formData.flagSemNotaFiscal === '1';

  const validateNotaFiscalRule = () => {
    const hasNotaFiscal =
      String(formData.numrNotaFiscalPrincipal || '').trim().length > 0 ||
      String(formData.numrNotaFiscalSecundaria || '').trim().length > 0;

    // Regra de negocio: ou informa ao menos uma nota fiscal, ou marca a flag sem nota.
    return hasNotaFiscal || isSemNotaFiscal;
  };

  // Exige escolher um valor valido nos flags (nao deixar no placeholder "Selecione").
  // Sem isso o backend recebe 0/-1 invalido e responde 500.
  const validateFlagsRule = () => {
    const flagErrors = {};
    // Adição de água: "Não informado" (0) é uma opção válida — não exige seleção.
    if (String(formData.flagIev) === '-1' || formData.flagIev === '') {
      flagErrors.flagIev = 'Selecione o IEV.';
    }
    return flagErrors;
  };

  // Garante a ordem cronológica das horas de descarga (só entre as horas preenchidas):
  // saída usina → chegada obra → início descarga → fim descarga → chegada usina.
  const validateHorariosDescargaRule = () => {
    const sequencia = [
      { field: 'dataSaidaUsina', label: 'saída da usina' },
      { field: 'dataChegadaObra', label: 'chegada na obra' },
      { field: 'dataInicioDescarga', label: 'início da descarga' },
      { field: 'dataFimDescarga', label: 'fim da descarga' },
      { field: 'dataChegadaUsina', label: 'chegada na usina' },
    ];

    const horarioErrors = {};
    let anterior = null; // { minutes, label } da última hora válida preenchida
    sequencia.forEach((item) => {
      const minutes = parseHoraToMinutes(formData[item.field]);
      if (minutes === null) return; // hora vazia — ignora
      if (anterior && minutes < anterior.minutes) {
        horarioErrors[item.field] = `A hora de ${item.label} não pode ser anterior à de ${anterior.label}.`;
      } else {
        anterior = { minutes, label: item.label };
      }
    });

    return horarioErrors;
  };

  // Impede repetir, na MESMA concretagem, nota fiscal (principal/secundária, no mesmo balde)
  // e lacre entre caminhões diferentes. Retorna mapa de erros por campo.
  const validateDocumentosUnicosRule = () => {
    const docErrors = {};
    const concretagemId = String(formData.concretagemRealizadaId || '');
    if (!concretagemId) return docErrors;

    // Demais caminhões da mesma concretagem (exclui o que está sendo editado).
    const outros = registros.filter(
      (r) => String(r.idConcretagem) === concretagemId && String(r.id) !== String(editingCaminhaoId || '')
    );

    // Notas fiscais já usadas (principal e secundária no mesmo conjunto).
    const notasUsadas = new Set();
    outros.forEach((r) => {
      [normalizeNotaFiscalValue(r.nfPrincipal), normalizeNotaFiscalValue(r.nfSecundaria)]
        .forEach((nf) => { if (nf) notasUsadas.add(nf); });
    });

    const nfPrincipal = normalizeNotaFiscalValue(formData.numrNotaFiscalPrincipal);
    const nfSecundaria = normalizeNotaFiscalValue(formData.numrNotaFiscalSecundaria);

    if (nfPrincipal && nfSecundaria && nfPrincipal === nfSecundaria) {
      docErrors.numrNotaFiscalSecundaria = 'A nota fiscal secundária não pode ser igual à principal.';
    }
    if (nfPrincipal && notasUsadas.has(nfPrincipal)) {
      docErrors.numrNotaFiscalPrincipal = 'Esta nota fiscal já foi usada em outro caminhão desta concretagem.';
    }
    if (nfSecundaria && !docErrors.numrNotaFiscalSecundaria && notasUsadas.has(nfSecundaria)) {
      docErrors.numrNotaFiscalSecundaria = 'Esta nota fiscal já foi usada em outro caminhão desta concretagem.';
    }

    // Lacres já usados (comparação case-insensitive).
    const lacresUsados = new Set(
      outros.map((r) => String(r.lacre || '').trim().toLowerCase()).filter(Boolean)
    );
    const lacre = String(formData.numrLacreCaminhao || '').trim();
    if (lacre && lacresUsados.has(lacre.toLowerCase())) {
      docErrors.numrLacreCaminhao = 'Este lacre já foi usado em outro caminhão desta concretagem.';
    }

    return docErrors;
  };

  // Quando marcado, limpa e bloqueia as notas para evitar envio acidental de valores antigos.
  const handleSemNotaFiscalChange = (event) => {
    const isChecked = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      flagSemNotaFiscal: isChecked ? '1' : '0',
      numrNotaFiscalPrincipal: isChecked ? '' : prev.numrNotaFiscalPrincipal,
      numrNotaFiscalSecundaria: isChecked ? '' : prev.numrNotaFiscalSecundaria,
    }));

    setErrors((prev) => {
      if (!prev.notaFiscalRegra) return prev;
      const { notaFiscalRegra: _removed, ...rest } = prev;
      return rest;
    });
  };

  // Seleciona a concretagem do passo 1, preenche empresa/obra/data e avança para o passo 2.
  const handleSelectConcretagem = (item) => {
    setSelectedConcretagem(item);
    setErrors((prev) => {
      const { concretagemRealizadaId, ...rest } = prev;
      return rest;
    });
    setFormData((prev) => ({
      ...prev,
      concretagemRealizadaId: item.id,
      empresa: item.empresa,
      obra: item.obra,
      dataConcretagem: item.data,
    }));
    setCurrentStep(2);
  };

  // Clique no card: seleciona a concretagem ou, se já for a selecionada, desfaz a seleção (e o filtro da tabela).
  const handleToggleConcretagem = (item) => {
    if (String(selectedConcretagem?.id) !== String(item.id)) {
      handleSelectConcretagem(item);
      return;
    }
    setSelectedConcretagem(null);
    setFormData((prev) => ({
      ...prev,
      concretagemRealizadaId: '',
      empresa: '',
      obra: '',
      dataConcretagem: '',
    }));
  };

  // Atalhos vindos da Gestão de concretagem: ?concretagem=ID abre o wizard já na concretagem
  // escolhida; ?caminhao=ID abre a edição daquele caminhão para completar os CPs.
  useEffect(() => {
    const idConcretagem = searchParams.get('concretagem');
    const idCaminhao = searchParams.get('caminhao');
    if (!idConcretagem && !idCaminhao) return;
    if (idConcretagem && loadingConcretagens) return;
    if (idCaminhao && loading) return;

    setSearchParams({}, { replace: true });

    if (idCaminhao) {
      const registro = registros.find((item) => String(item.id) === String(idCaminhao));
      if (registro) openEditCaminhao(registro);
      return;
    }

    const concretagem = concretagensDisponiveis.find((item) => String(item.id) === String(idConcretagem));
    if (!concretagem) return;
    handleSelectConcretagem(concretagem);
    setEditingCaminhaoId(null);
    setIsWizardOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, concretagensDisponiveis, registros, loading, loadingConcretagens]);

  // Avança o wizard para `step` sem retroceder caso já esteja além dele.
  const handleAdvance = (step) => setCurrentStep((prev) => (prev > step ? prev : step));

  // Valida os campos obrigatorios de classificacao antes de sair do passo 2.
  const handleAdvanceFromStep2 = () => {
    const obrigatorios = [
      ['etapaObra', 'Etapa da obra obrigatória.'],
      ['tipoCimento', 'Tipo de cimento obrigatório.'],
      ['tipoConcreto', 'Tipo de concreto obrigatório.'],
      ['concreteira', 'Concreteira obrigatória.'],
    ];
    const stepErrors = {};
    obrigatorios.forEach(([field, message]) => {
      if (!String(formData[field] || '').trim()) stepErrors[field] = message;
    });

    if (Object.keys(stepErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }

    handleAdvance(3);
  };

  // Valida a regra de nota fiscal antes de sair do passo 3.
  const handleAdvanceFromStep3 = () => {
    if (!validateNotaFiscalRule()) {
      setErrors((prev) => ({
        ...prev,
        notaFiscalRegra: 'Informe pelo menos uma nota fiscal ou marque a opção sem nota fiscal.',
      }));
      return;
    }

    const documentoErrors = validateDocumentosUnicosRule();
    if (Object.keys(documentoErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...documentoErrors }));
      return;
    }

    setErrors((prev) => {
      if (!prev.notaFiscalRegra) return prev;
      const { notaFiscalRegra: _removed, ...rest } = prev;
      return rest;
    });

    handleAdvance(4);
  };

  // Valida os flags obrigatorios antes de sair do passo 4.
  const handleAdvanceFromStep4 = () => {
    const flagErrors = validateFlagsRule();
    if (Object.keys(flagErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...flagErrors }));
      return;
    }
    handleAdvance(5);
  };

  // Valida a ordem cronológica das horas de descarga antes de sair do passo 5.
  const handleAdvanceFromStep5 = () => {
    const horarioErrors = validateHorariosDescargaRule();
    if (Object.keys(horarioErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...horarioErrors }));
      return;
    }
    handleAdvance(6);
  };

  // Monta a chave de erro de um campo de uma linha de slump (slump_<i>_<campo>).
  const getSlumpErrorKey = (index, field) => `slump_${index}_${field}`;

  // Valida as linhas de slump (água, aditivo e valor obrigatórios); retorna mapa de erros por célula.
  const validateSlumpRows = (rows) => {
    const slumpFieldErrors = {};

    rows.forEach((row, index) => {
      if (row.valrAdicaoAgua === '') {
        slumpFieldErrors[getSlumpErrorKey(index, 'valrAdicaoAgua')] = 'Adição de água obrigatória.';
      }
      if (row.valrAdicaoAditivo === '') {
        slumpFieldErrors[getSlumpErrorKey(index, 'valrAdicaoAditivo')] = 'Adição de aditivo obrigatória.';
      }
      if (row.valrSlump === '') {
        slumpFieldErrors[getSlumpErrorKey(index, 'valrSlump')] = 'Valor slump obrigatório.';
      }
    });

    return slumpFieldErrors;
  };

  // Atualiza um campo de uma linha de slump e limpa o erro daquela célula.
  const handleSlumpChange = (index, field) => (event) => {
    const value = event.target.value;
    setSlumpRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));

    const slumpErrorKey = getSlumpErrorKey(index, field);
    setErrors((prev) => {
      if (!prev[slumpErrorKey]) return prev;
      const { [slumpErrorKey]: _removed, ...rest } = prev;
      return rest;
    });
  };

  // Incrementa/decrementa a quantidade de testes de slump dentro das opções permitidas.
  const handleAdjustQuantidadeSlump = (direction) => () => {
    const current = Number(formData.quantidadeSlump);
    const currentIndex = quantidadeSlumpOptions.findIndex((option) => option === current);
    const lastIndex = quantidadeSlumpOptions.length - 1;

    let nextValue = quantidadeSlumpOptions[0];

    if (direction === 'increment') {
      nextValue = currentIndex === -1 ? quantidadeSlumpOptions[0] : quantidadeSlumpOptions[Math.min(currentIndex + 1, lastIndex)];
    } else if (currentIndex > 0) {
      nextValue = quantidadeSlumpOptions[currentIndex - 1];
    }

    setFormData((prev) => ({ ...prev, quantidadeSlump: String(nextValue) }));
    setErrors((prev) => {
      const { quantidadeSlump, ...rest } = prev;
      return rest;
    });
  };

  // Atalho: soma `amount` à quantidade de testes de slump, respeitando o máximo permitido.
  const handleAddQuantidadeSlump = (amount) => () => {
    const maximo = quantidadeSlumpOptions[quantidadeSlumpOptions.length - 1];
    const current = Number(formData.quantidadeSlump) || 0;
    const nextValue = Math.min(current + amount, maximo);
    setFormData((prev) => ({ ...prev, quantidadeSlump: String(nextValue) }));
    setErrors((prev) => {
      const { quantidadeSlump, ...rest } = prev;
      return rest;
    });
  };

  // Valida a quantidade e as linhas de slump antes de sair do passo 6.
  const handleAdvanceFromStep6 = () => {
    if (!Number(formData.quantidadeSlump)) {
      setErrors((prev) => ({
        ...prev,
        quantidadeSlump: 'Quantidade de testes slump obrigatória.',
      }));
      return;
    }

    const slumpFieldErrors = validateSlumpRows(slumpRows);
    if (Object.keys(slumpFieldErrors).length > 0) {
      setErrors(slumpFieldErrors);
      return;
    }

    handleAdvance(7);
  };

  // Monta a chave de erro de um campo de uma linha de CP (cp_<i>_<campo>).
  const getCpErrorKey = (index, field) => `cp_${index}_${field}`;

  // Valida as linhas de corpos de prova (idade, etiqueta e tipo obrigatórios); retorna mapa de erros por célula.
  const validateCpRows = (rows) => {
    const cpFieldErrors = {};

    rows.forEach((row, index) => {
      const idade = Number(row.idadeRuptura);
      if (!row.idadeRuptura || !Number.isFinite(idade) || idade <= 0) {
        cpFieldErrors[getCpErrorKey(index, 'idadeRuptura')] = 'Idade de ruptura obrigatória.';
      }

      if (!String(row.etiqueta || '').trim()) {
        cpFieldErrors[getCpErrorKey(index, 'etiqueta')] = 'Etiqueta obrigatória.';
      }

      if (!String(row.tipo || '').trim()) {
        cpFieldErrors[getCpErrorKey(index, 'tipo')] = 'Tipo obrigatório.';
      }
    });

    return cpFieldErrors;
  };

  // Atualiza um campo de uma linha de CP; ao editar a 1ª etiqueta, gera sugestões de etiqueta para as demais.
  const handleCpChange = (index, field) => (event) => {
    const value = event.target.value;
    // If editing etiqueta
    if (field === 'etiqueta') {
      // update the row value immediately for the edited index
      setCpRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));

      // If editing first etiqueta, generate suggestions for the following rows (do not apply them)
      if (index === 0) {
        const trimmed = String(value || '').trim();
        if (!trimmed) {
          setCpSuggestions([]);
        } else {
          const m = trimmed.match(/^(.*?)(\d+)$/);
          const hasNumericSuffix = !!m;
          const prefix = hasNumericSuffix ? m[1] : `${trimmed}-`;
          const startNum = hasNumericSuffix ? Number(m[2]) : null;
          const padLength = hasNumericSuffix ? m[2].length : 0;

          setCpSuggestions((prev) => {
            const nextSuggestions = Array.from({ length: cpRows.length }).map(() => '');
            for (let i = 1; i < cpRows.length; i++) {
              // only suggest if the field is currently empty
              const cur = String(cpRows[i]?.etiqueta || '').trim();
              if (cur) { nextSuggestions[i] = ''; continue; }

              if (hasNumericSuffix && Number.isFinite(startNum)) {
                const num = startNum + i;
                const numStr = String(num).padStart(padLength, '0');
                nextSuggestions[i] = `${prefix}${numStr}`;
              } else {
                nextSuggestions[i] = `${prefix}${i + 1}`;
              }
            }
            return nextSuggestions;
          });
        }

        // remove etiqueta-related errors
        setErrors((prev) => {
          const copy = { ...prev };
          Object.keys(copy).forEach((k) => { if (k.startsWith('cp_') && k.endsWith('_etiqueta')) delete copy[k]; });
          return copy;
        });
      } else {
        // If user typed into a non-first etiqueta, clear suggestion for this index
        setCpSuggestions((prev) => {
          if (!prev || prev.length === 0) return prev;
          const next = [...prev];
          next[index] = '';
          return next;
        });
      }

      return;
    }

    // Generic field update — se for o tipo do 1o CP e a flag estiver ligada, replica para todos.
    if (field === 'tipo' && index === 0 && aplicarTipoTodosCp) {
      setCpRows((prev) => prev.map((row) => ({ ...row, tipo: value })));
    } else {
      setCpRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));
    }

    const cpErrorKey = getCpErrorKey(index, field);
    setErrors((prev) => {
      if (!prev[cpErrorKey]) return prev;
      const { [cpErrorKey]: _removed, ...rest } = prev;
      return rest;
    });
  };

  // Liga/desliga a replicação do tipo do 1o CP; ao ligar, aplica imediatamente a todos.
  const handleToggleAplicarTipoTodos = (event) => {
    const checked = event.target.checked;
    setAplicarTipoTodosCp(checked);
    if (checked) {
      setCpRows((prev) => {
        const tipoPrimeiro = prev[0]?.tipo || '';
        return prev.map((row, i) => (i === 0 ? row : { ...row, tipo: tipoPrimeiro }));
      });
      setErrors((prev) => {
        const copy = { ...prev };
        Object.keys(copy).forEach((k) => { if (k.startsWith('cp_') && k.endsWith('_tipo')) delete copy[k]; });
        return copy;
      });
    }
  };

  // Ao pressionar Tab numa etiqueta vazia, aceita a sugestão e move o foco para a próxima.
  const handleEtiquetaKeyDown = (event, index) => {
    if (event.key === 'Tab') {
      const suggestion = cpSuggestions[index];
      const current = String(cpRows[index]?.etiqueta || '').trim();
      if (suggestion && !current) {
        event.preventDefault();
        // apply suggestion
        setCpRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, etiqueta: suggestion } : row)));
        setCpSuggestions((prev) => {
          if (!prev) return prev;
          const next = [...prev];
          next[index] = '';
          return next;
        });
        // move focus to next etiqueta input if exists
        const nextRef = cpEtiquetaRefs.current[index + 1];
        if (nextRef && typeof nextRef.focus === 'function') {
          nextRef.focus();
        }
      }
    }
  };

  // Incrementa/decrementa a quantidade de corpos de prova dentro das opções permitidas.
  const handleAdjustQuantidadeCp = (direction) => () => {
    const current = Number(formData.quantidadeCp);
    const currentIndex = quantidadeCpOptions.findIndex((option) => option === current);
    const lastIndex = quantidadeCpOptions.length - 1;

    let nextValue = quantidadeCpOptions[0];

    if (direction === 'increment') {
      nextValue = currentIndex === -1 ? quantidadeCpOptions[0] : quantidadeCpOptions[Math.min(currentIndex + 1, lastIndex)];
    } else if (currentIndex > 0) {
      nextValue = quantidadeCpOptions[currentIndex - 1];
    }

    setFormData((prev) => ({ ...prev, quantidadeCp: String(nextValue) }));
    setErrors((prev) => {
      const { quantidadeCp, ...rest } = prev;
      return rest;
    });
  };

  // Atalho: soma `amount` à quantidade de CP, respeitando o máximo permitido.
  const handleAddQuantidadeCp = (amount) => () => {
    const maximo = quantidadeCpOptions[quantidadeCpOptions.length - 1];
    const current = Number(formData.quantidadeCp) || 0;
    const nextValue = Math.min(current + amount, maximo);
    setFormData((prev) => ({ ...prev, quantidadeCp: String(nextValue) }));
    setErrors((prev) => {
      const { quantidadeCp, ...rest } = prev;
      return rest;
    });
  };

  // Fecha o wizard (mantém o rascunho em cache).
  const closeWizard = () => {
    setIsWizardOpen(false);
  };

  // Abre o wizard para um novo caminhão; exige uma concretagem selecionada.
  const handleOpenWizard = () => {
    setSaveMessage('');
    if (!selectedConcretagem) {
      setErrors((prev) => ({ ...prev, concretagemRealizadaId: 'Selecione uma concretagem para iniciar o cadastro do caminhão.' }));
      return;
    }
    // Nao permite cadastrar mais caminhões do que a quantidade prevista na concretagem.
    const limite = Number(selectedConcretagem.quantidadeCaminhao) || 0;
    const jaCadastrados = registros.filter((r) => String(r.idConcretagem) === String(selectedConcretagem.id)).length;
    if (limite > 0 && jaCadastrados >= limite) {
      setErrors((prev) => ({
        ...prev,
        concretagemRealizadaId: `Esta concretagem prevê ${limite} caminhão(ões) e todos já foram cadastrados.`,
      }));
      return;
    }
    setEditingCaminhaoId(null);
    setCurrentStep(2);
    setIsWizardOpen(true);
  };

  // Busca o caminhão completo na API e preenche o wizard (form, linhas de slump e CP) para edição.
  const openEditCaminhao = async (item) => {
    setSaveMessage('');
    try {
      const detail = await getCaminhaoById(item.id);
      setEditingCaminhaoId(item.id);

      const concretagemId = String(detail.concretagemRealizada?.idConcretagemRealizada || detail.idConcretagemRealizada || item.idConcretagem || '');
      const concretagem = concretagensDisponiveis.find((c) => String(c.id) === concretagemId) || null;
      if (concretagem) {
        setSelectedConcretagem(concretagem);
      }

      const hasNF = (detail.numrNotaFiscalPrincipal && detail.numrNotaFiscalPrincipal !== 0) ||
        (detail.numrNotaFiscalSecundaria && detail.numrNotaFiscalSecundaria !== 0);

      setFormData({
        concretagemRealizadaId: concretagemId,
        empresa: detail.concretagemRealizada?.obra?.empresa?.razaoSocial || detail.empresa || item.empresa || '',
        obra: detail.concretagemRealizada?.obra?.nomeObra || detail.obra || item.obra || '',
        dataConcretagem: normalizeDateOnly(detail.concretagemRealizada?.dataConcretagem || detail.dataConcretagem || item.dataConcretagem || ''),
        placaCaminhao: detail.placaCaminhao || '',
        etapaObra: String(detail.etapaObra?.idEtapaObra || detail.idEtapaObra || ''),
        tipoCimento: String(detail.tipoCimento?.idTipoCimento || detail.idTipoCimento || ''),
        tipoConcreto: String(detail.tipoConcreto?.idTipoConcreto || detail.idTipoConcreto || ''),
        concreteira: String(detail.concreteira?.idConcreteira || detail.idConcreteira || ''),
        cartaTraco: String(detail.cartaTraco?.idCartaTraco || detail.idCartaTraco || ''),
        localConcretagem: detail.localConcretagem || '',
        valrClasResiCompressao: detail.valrClasResiCompressao !== null && detail.valrClasResiCompressao !== undefined ? String(detail.valrClasResiCompressao) : '',
        valrClasResiTracaoFlexao: detail.valrClasResiTracaoFlexao !== null && detail.valrClasResiTracaoFlexao !== undefined ? String(detail.valrClasResiTracaoFlexao) : '',
        valrClasResiTracaoIndireta: detail.valrClasResiTracaoIndireta !== null && detail.valrClasResiTracaoIndireta !== undefined ? String(detail.valrClasResiTracaoIndireta) : '',
        valrClasResiTracaoDireta: detail.valrClasResiTracaoDireta !== null && detail.valrClasResiTracaoDireta !== undefined ? String(detail.valrClasResiTracaoDireta) : '',
        valrClasTrabalhabiilidade: detail.valrClasTrabalhabilidade !== null && detail.valrClasTrabalhabilidade !== undefined ? String(detail.valrClasTrabalhabilidade) : '',
        numrNotaFiscalPrincipal: detail.numrNotaFiscalPrincipal !== null && detail.numrNotaFiscalPrincipal !== 0 && detail.numrNotaFiscalPrincipal !== undefined ? String(detail.numrNotaFiscalPrincipal) : '',
        numrNotaFiscalSecundaria: detail.numrNotaFiscalSecundaria !== null && detail.numrNotaFiscalSecundaria !== 0 && detail.numrNotaFiscalSecundaria !== undefined ? String(detail.numrNotaFiscalSecundaria) : '',
        numrLacreCaminhao: detail.numrLacreCaminhao || '',
        valrVolume: detail.valrVolume !== null && detail.valrVolume !== undefined ? String(detail.valrVolume) : '',
        flagSemNotaFiscal: hasNF ? '0' : '1',
        flagContAdicaoAgua: String(detail.flagContAdicaoAgua || '0'),
        flagIev: String(detail.flagIev !== null && detail.flagIev !== undefined ? detail.flagIev : '-1'),
        dataSaidaUsina: detail.dataSaidaUsina ? formatHour(detail.dataSaidaUsina) : '',
        dataChegadaUsina: detail.dataChegadaUsina ? formatHour(detail.dataChegadaUsina) : '',
        dataChegadaObra: detail.dataChegadaObra ? formatHour(detail.dataChegadaObra) : '',
        dataInicioDescarga: detail.dataInicioDescarga ? formatHour(detail.dataInicioDescarga) : '',
        dataFimDescarga: detail.dataFimDescarga ? formatHour(detail.dataFimDescarga) : '',
        valrTemperaturaConcreto: detail.valrTemperaturaConcreto !== null && detail.valrTemperaturaConcreto !== undefined ? String(detail.valrTemperaturaConcreto) : '',
        valrTemperaturaAmbiente: detail.valrTemperaturaAmbiente !== null && detail.valrTemperaturaAmbiente !== undefined ? String(detail.valrTemperaturaAmbiente) : '',
        quantidadeSlump: String(detail.testeSlumps?.length || ''),
        observacoes: detail.observacao || '',
        quantidadeCp: String(detail.corpoProvas?.length || ''),
      });

      if (detail.corpoProvas && Array.isArray(detail.corpoProvas)) {
        setCpRows(detail.corpoProvas.map(cp => ({
          idadeRuptura: cp.idadeRuptura !== null && cp.idadeRuptura !== undefined ? String(cp.idadeRuptura) : '',
          etiqueta: cp.idenObraEtiqueta || '',
          tipo: cp.idTipoCorpoProva !== null && cp.idTipoCorpoProva !== undefined ? String(cp.idTipoCorpoProva) : '',
        })));
      } else {
        setCpRows([]);
      }

      if (detail.testeSlumps && Array.isArray(detail.testeSlumps)) {
        setSlumpRows(detail.testeSlumps.map(ts => ({
          valrSlump: ts.valrSlump !== null && ts.valrSlump !== undefined ? String(ts.valrSlump) : '',
          valrAdicaoAgua: ts.valrAdicaoAgua !== null && ts.valrAdicaoAgua !== undefined ? String(ts.valrAdicaoAgua) : '',
          valrAdicaoAditivo: ts.valrAdicaoAditivo !== null && ts.valrAdicaoAditivo !== undefined ? String(ts.valrAdicaoAditivo) : '',
        })));
      } else {
        setSlumpRows([]);
      }

      setCurrentStep(2);
      setIsWizardOpen(true);
    } catch (error) {
      console.error("Erro ao carregar caminhão para edição:", error);
      showErrorToast("Não foi possível carregar os detalhes do caminhão.");
    }
  };

  // Busca o caminhão completo e abre o painel de visualização (somente leitura).
  const openViewCaminhao = async (item) => {
    try {
      const detail = await getCaminhaoById(item.id);
      setViewData({ ...detail, _listItem: item });
    } catch (error) {
      showErrorToast('Não foi possível carregar os detalhes do caminhão.');
    }
  };

  // Resolve o nome de uma FK do caminhão: procura na lista de opções pelo id e,
  // se não achar, cai para os campos de nome do objeto aninhado retornado pela API.
  const resolveViewLabel = (options, id, nestedObj, nestedKeys = []) => {
    const found = Array.isArray(options) ? options.find((o) => String(o.id) === String(id)) : null;
    if (found) return found.label;
    for (const key of nestedKeys) {
      const value = nestedObj?.[key];
      if (value !== undefined && value !== null && String(value).trim()) return String(value).trim();
    }
    return '-';
  };

  // Limpa todo o estado do wizard (form, slump, CP e cache de rascunho).
  const resetWizard = () => {
    setCurrentStep(2);
    setErrors({});
    setSaveMessage('');
    setFormData(INITIAL_FORM_DATA);
    setSlumpRows([]);
    setCpRows([]);
    setAplicarTipoTodosCp(false);
    caminhoesDraftCache = null;
  };

  // Pede confirmação antes de limpar o cadastro em andamento.
  const handleConfirmResetWizard = () => {
    const confirmed = window.confirm('Tem certeza que deseja limpar o cadastro em andamento?');
    if (!confirmed) return;
    resetWizard();
  };

  // Valida todas as etapas (schema, nota fiscal, slump e CP) e cria/atualiza o caminhão via service.
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingRef.current) return; // ja esta salvando, ignora o clique extra
    setSaveMessage('');

    const result = schema.safeParse({
      concretagemRealizadaId: formData.concretagemRealizadaId,
      empresa: formData.empresa,
      obra: formData.obra,
      dataConcretagem: formData.dataConcretagem,
      etapaObra: formData.etapaObra,
      tipoCimento: formData.tipoCimento,
      tipoConcreto: formData.tipoConcreto,
      concreteira: formData.concreteira,
      quantidadeCp: Number(formData.quantidadeCp),
    });

    if (!result.success) {
      const nextErrors = {};
      result.error.issues.forEach((issue) => { nextErrors[issue.path[0]] = issue.message; });
      setErrors(nextErrors);
      // Leva o usuario a etapa de classificacao se algum desses campos estiver invalido.
      if (['etapaObra', 'tipoCimento', 'tipoConcreto', 'concreteira'].some((f) => nextErrors[f])) {
        setCurrentStep((prev) => (prev < 2 ? 2 : prev));
      }
      return;
    }

    // Ao criar (nao ao editar), respeita a quantidade de caminhões prevista na concretagem.
    if (!editingCaminhaoId) {
      const limite = Number(selectedConcretagem?.quantidadeCaminhao) || 0;
      const jaCadastrados = registros.filter((r) => String(r.idConcretagem) === String(formData.concretagemRealizadaId)).length;
      if (limite > 0 && jaCadastrados >= limite) {
        setSaveMessage(`Esta concretagem prevê ${limite} caminhão(ões) e todos já foram cadastrados.`);
        return;
      }
    }

    if (!validateNotaFiscalRule()) {
      setErrors({ notaFiscalRegra: 'Informe pelo menos uma nota fiscal ou marque a opção sem nota fiscal.' });
      setCurrentStep(3);
      return;
    }

    const documentoErrors = validateDocumentosUnicosRule();
    if (Object.keys(documentoErrors).length > 0) {
      setErrors(documentoErrors);
      setCurrentStep(3);
      return;
    }

    const flagErrors = validateFlagsRule();
    if (Object.keys(flagErrors).length > 0) {
      setErrors(flagErrors);
      setCurrentStep(4);
      return;
    }

    const horarioErrors = validateHorariosDescargaRule();
    if (Object.keys(horarioErrors).length > 0) {
      setErrors(horarioErrors);
      setCurrentStep(5);
      return;
    }

    if (!Number(formData.quantidadeSlump)) {
      setErrors({ quantidadeSlump: 'Quantidade de testes slump obrigatória.' });
      setCurrentStep(6);
      return;
    }

    const slumpFieldErrors = validateSlumpRows(slumpRows);
    if (Object.keys(slumpFieldErrors).length > 0) {
      setErrors(slumpFieldErrors);
      setCurrentStep(6);
      return;
    }

    const cpFieldErrors = validateCpRows(cpRows);
    if (Object.keys(cpFieldErrors).length > 0) {
      setErrors(cpFieldErrors);
      setCurrentStep(7);
      return;
    }

    setErrors({});
    savingRef.current = true;
    setIsSaving(true);

    try {
      // Passamos o formData e os cpRows brutos. O Service resolve o payload.
      if (editingCaminhaoId) {
        await updateCaminhao(editingCaminhaoId, formData, cpRows, slumpRows);
        // Otimisticamente atualiza a listagem com os dados do form.
        setRegistros((prev) => prev.map((r) => (String(r.id) === String(editingCaminhaoId) ? ({
          ...r,
          idConcretagem: formData.concretagemRealizadaId,
          obra: formData.obra,
          empresa: formData.empresa,
          placaCaminhao: formData.placaCaminhao || r.placaCaminhao,
          notaFiscal: String(formData.numrNotaFiscalPrincipal || '').trim() || String(formData.numrNotaFiscalSecundaria || '').trim() || (formData.flagSemNotaFiscal === '1' ? 'Sem nota fiscal' : r.notaFiscal),
          nfPrincipal: normalizeNotaFiscalValue(formData.numrNotaFiscalPrincipal),
          nfSecundaria: normalizeNotaFiscalValue(formData.numrNotaFiscalSecundaria),
          lacre: String(formData.numrLacreCaminhao || '').trim(),
          dataConcretagem: formData.dataConcretagem,
          quantidadeCp: Number(formData.quantidadeCp),
        }) : r)));

        showSuccessToast('Caminhão atualizado com sucesso.');
        setEditingCaminhaoId(null);
        resetWizard();
        setIsWizardOpen(false);
      } else {
        const createResult = await createCaminhao(formData, cpRows, slumpRows);
        const createdId = extractCreatedCaminhaoId(createResult?.response) || extractCreatedCaminhaoId(createResult);

        const novoRegistro = {
          id: createdId ? String(createdId) : `truck-${Date.now()}`,
          idConcretagem: formData.concretagemRealizadaId,
          obra: formData.obra,
          empresa: formData.empresa,
          placaCaminhao: formData.placaCaminhao || '-',
          notaFiscal: String(formData.numrNotaFiscalPrincipal || '').trim()
            || String(formData.numrNotaFiscalSecundaria || '').trim()
            || (formData.flagSemNotaFiscal === '1' ? 'Sem nota fiscal' : '-'),
          nfPrincipal: normalizeNotaFiscalValue(formData.numrNotaFiscalPrincipal),
          nfSecundaria: normalizeNotaFiscalValue(formData.numrNotaFiscalSecundaria),
          lacre: String(formData.numrLacreCaminhao || '').trim(),
          dataConcretagem: formData.dataConcretagem,
          quantidadeCp: Number(formData.quantidadeCp),
          status: 'Concluido',
        };

        setRegistros((prev) => [novoRegistro, ...prev]);

        try {
          const freshData = await getCaminhao();
          if (Array.isArray(freshData)) {
            const normalized = freshData.map(normalizeCaminhaoRegistro).filter((item) => item.id);
            setRegistros(normalized);
          }
        } catch (_) { }

        showSuccessToast('Caminhão cadastrado com sucesso.');
        resetWizard();
        setIsWizardOpen(false);
      }
    } catch (error) {
      const apiMessage = error?.data?.message || error?.message || 'Não foi possível salvar o caminhão.';
      setSaveMessage(apiMessage);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  // Exclui o caminhão após confirmação e remove da lista local.
  const handleDelete = async (id) => {
    const confirm = window.confirm('Deseja realmente excluir este caminhão?');
    if (!confirm) return;

    // Segunda confirmacao: alerta que os resultados dos CPs do caminhao tambem serao excluidos.
    const confirmResultados = window.confirm(
      'Atenção: os resultados lançados associados aos CPs deste caminhão também serão excluídos.\n\nDeseja continuar?'
    );
    if (!confirmResultados) return;

    try {
      await deleteCaminhao(id);
      setRegistros((prev) => prev.filter((r) => String(r.id) !== String(id)));
      showSuccessToast('Caminhão excluído com sucesso.');
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível excluir o caminhão.');
    }
  };

  const quantidadeSelecionada = Number(formData.quantidadeCp);
  const quantidadeSelecionadaIndex = quantidadeCpOptions.findIndex((option) => option === quantidadeSelecionada);
  const isMinusDisabled = quantidadeSelecionadaIndex <= 0;
  const isPlusDisabled = quantidadeSelecionadaIndex === quantidadeCpOptions.length - 1;

  const quantidadeSlumpSelecionada = Number(formData.quantidadeSlump);
  const quantidadeSlumpSelecionadaIndex = quantidadeSlumpOptions.findIndex((option) => option === quantidadeSlumpSelecionada);
  const isMinusSlumpDisabled = quantidadeSlumpSelecionadaIndex <= 0;
  const isPlusSlumpDisabled = quantidadeSlumpSelecionadaIndex === quantidadeSlumpOptions.length - 1;

  // Controle do limite de caminhões previsto na concretagem selecionada.
  const limiteCaminhoes = Number(selectedConcretagem?.quantidadeCaminhao) || 0;
  const caminhoesCadastradosNaConcretagem = selectedConcretagem
    ? registros.filter((r) => String(r.idConcretagem) === String(selectedConcretagem.id)).length
    : 0;
  const limiteCaminhoesAtingido = !!selectedConcretagem && limiteCaminhoes > 0 && caminhoesCadastradosNaConcretagem >= limiteCaminhoes;

  return (
    <div className="caminhoes-page space-y-6">
      <section className="caminhoes-header rounded-2xl bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de caminhões</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Visualize os cadastros e inicie um novo registro em blocos por etapa.
          </p>
        </div>
      </section>

      <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e7490]/10 text-sm font-semibold text-[#0e7490]">
            {selectedConcretagem ? <Check className="h-4 w-4" /> : '1'}
          </span>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Concretagem realizada</h2>
            <p className="text-sm text-slate-500">
              Esse bloco funciona como filtro principal e também define a concretagem antes do cadastro do caminhão.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Empresa <span className="text-rose-500">*</span></label>
            <select
              value={filters.empresa}
              onChange={handleFilterChange('empresa')}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
            >
              <option value="">Todas</option>
              {empresaOptions.map((empresa) => (
                <option key={empresa.idEmpresa} value={empresa.razaoSocial}>
                  {empresa.razaoSocial}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Obra <span className="text-rose-500">*</span></label>
            <select
              value={filters.obra}
              onChange={handleFilterChange('obra')}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
            >
              <option value="">Todas</option>
              {obraOptions.map((obra) => (
                <option key={obra} value={obra}>
                  {obra}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Data:</label>
            <input
              type="date"
              value={filters.data}
              onChange={handleFilterChange('data')}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
            />
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {loadingConcretagens ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Carregando concretagens...
            </div>
          ) : (!filters.empresa || !filters.obra) ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Selecione uma empresa e uma obra para visualizar as concretagens.
            </div>
          ) : filteredConcretagens.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Nenhuma concretagem encontrada com os filtros atuais.
            </div>
          ) : (
            filteredConcretagens.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => handleToggleConcretagem(item)}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition ${selectedConcretagem?.id === item.id
                  ? 'border-[#0e7490] bg-[#0e7490]/5 text-[#0e7490]'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
              >
                <div>
                  <p className="font-medium text-slate-900">{item.obra}</p>
                  <p className="text-xs text-slate-500">{item.empresa}</p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <CalendarClock className="h-4 w-4" />
                  {formatDateTimeBr(item.dataHoraConcretagem, item.data)}
                </div>
              </button>
            ))
          )}
        </div>
        {errors.concretagemRealizadaId ? (
          <p className="mt-2 text-xs text-rose-600">{errors.concretagemRealizadaId}</p>
        ) : null}
        <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-end sm:justify-between">
          <p className="text-xs text-slate-500">Resultados na tabela: {filteredRegistros.length}</p>
          <div className="flex flex-col items-stretch gap-1 sm:items-end">
            <button
              type="button"
              onClick={handleOpenWizard}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              disabled={!selectedConcretagem || limiteCaminhoesAtingido}
            >
              <Plus className="h-4 w-4" />
              Cadastrar caminhão
            </button>
            {selectedConcretagem && limiteCaminhoes > 0 ? (
              <p className={`text-xs ${limiteCaminhoesAtingido ? 'text-rose-600' : 'text-slate-500'} sm:text-right`}>
                {caminhoesCadastradosNaConcretagem} de {limiteCaminhoes} caminhão(ões) cadastrado(s)
                {limiteCaminhoesAtingido ? ' — limite atingido' : ''}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <section className="caminhoes-metrics grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        {/* <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Concluídos</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.concluidos}</p>
        </div>*/}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total de CP</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.totalCps}</p>
        </div>
      </section>

      <section className="caminhoes-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar em toda a listagem"
            resultCount={filteredRegistros.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Obra</th>
                <th className="px-4 py-3">Nota fiscal</th>
                <th className="px-4 py-3">Data da concretagem</th>
                <th className="px-4 py-3">CP's</th>
                <th className="px-4 py-3">Ações</th>
                <th className="px-4 py-3">
                  <ExpandableSearchFilter
                    variant="inline"
                    query={query}
                    onChange={setQuery}
                    placeholder="Buscar..."
                    resultCount={filteredRegistros.length}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 7 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredRegistros.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-slate-500">
                    Nenhum cadastro de caminhão encontrado.
                  </td>
                </tr>
              ) : (
                filteredRegistros.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4 font-medium text-slate-900">{item.empresa}</td>
                    <td className="px-4 py-4 text-slate-600">{item.obra}</td>
                    <td className="px-4 py-4 text-slate-600">{item.notaFiscal || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTimeBr(item.dataConcretagem)}</td>
                    <td className="px-4 py-4 text-slate-600">{item.quantidadeCp}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openViewCaminhao(item)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditCaminhao(item)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Excluir
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-4" />
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-skeleton-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-1/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredRegistros.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhum cadastro de caminhão encontrado.
            </div>
          ) : (
            filteredRegistros.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div>
                  <p className="text-base font-semibold text-slate-900">{item.obra}</p>
                  <p className="text-xs text-slate-500">{item.empresa}</p>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <span>Nota fiscal: {item.notaFiscal || '-'}</span>
                  <span>CP's: {item.quantidadeCp}</span>
                  <span className="col-span-2">Data da concretagem: {formatDateTimeBr(item.dataConcretagem)}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => openViewCaminhao(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditCaminhao(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {viewData ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setViewData(null)}
            aria-label="Fechar"
          />
          <div className="relative z-10 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Visualização</p>
                <h3 className="text-xl font-semibold text-slate-900">Detalhes do caminhão</h3>
                <p className="mt-1 text-sm text-slate-500">Dados registrados — somente leitura.</p>
              </div>
              <button
                type="button"
                onClick={() => setViewData(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 space-y-6">
              {/* Dados gerais */}
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Dados gerais</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { label: 'Empresa', value: viewData.concretagemRealizada?.obra?.empresa?.razaoSocial || viewData._listItem?.empresa || '-' },
                    { label: 'Obra', value: viewData.concretagemRealizada?.obra?.nomeObra || viewData._listItem?.obra || '-' },
                    { label: 'Data de concretagem', value: formatDateBr(normalizeDateOnly(viewData.concretagemRealizada?.dataConcretagem || '')) },
                    { label: 'Placa do caminhão', value: viewData.placaCaminhao || '-' },
                    { label: 'Local de concretagem', value: viewData.localConcretagem || '-' },
                    { label: 'Volume (m³)', value: viewData.valrVolume != null && viewData.valrVolume !== 0 ? viewData.valrVolume : '-' },
                    { label: 'NF / Remessa / Romaneio - Principal', value: viewData.numrNotaFiscalPrincipal && viewData.numrNotaFiscalPrincipal !== 0 ? viewData.numrNotaFiscalPrincipal : '-' },
                    { label: 'NF / Remessa / Romaneio - Secundário', value: viewData.numrNotaFiscalSecundaria && viewData.numrNotaFiscalSecundaria !== 0 ? viewData.numrNotaFiscalSecundaria : '-' },
                    { label: 'Sem nota fiscal', value: String(viewData.flagSemNotaFiscal) === '1' ? 'Sim' : 'Não' },
                    { label: 'Lacre do caminhão', value: viewData.numrLacreCaminhao || '-' },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
                      <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Classificação */}
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Classificação</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { label: 'Etapa da obra', value: resolveViewLabel(etapasObraOptions, viewData.etapaObra?.idEtapaObra || viewData.idEtapaObra, viewData.etapaObra, ['nomeEtapaObra', 'nome_etapa_obra', 'descricao', 'nome']) },
                    { label: 'Tipo de cimento', value: resolveViewLabel(tipoCimentoOptions, viewData.tipoCimento?.idTipoCimento || viewData.idTipoCimento, viewData.tipoCimento, ['nomeTipoCimento', 'nome_tipo_cimento', 'sigla', 'descricao', 'nome']) },
                    { label: 'Tipo de concreto', value: resolveViewLabel(tipoConcretoOptions, viewData.tipoConcreto?.idTipoConcreto || viewData.idTipoConcreto, viewData.tipoConcreto, ['nomeTipoConcreto', 'nome_tipo_concreto', 'descricao', 'nome']) },
                    { label: 'Concreteira', value: resolveViewLabel(concreteiraOptions, viewData.concreteira?.idConcreteira || viewData.idConcreteira, viewData.concreteira, ['nomeConcreteira', 'nome_concreteira', 'nome']) },
                    { label: 'Carta traço', value: resolveViewLabel(cartaTracoOptions, viewData.cartaTraco?.idCartaTraco || viewData.idCartaTraco, viewData.cartaTraco, ['nomeCartaTraco', 'nome_carta_traco', 'codgInternoDescricao', 'nome']) },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
                      <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Classificação de resistência */}
              {[
                viewData.valrClasResiCompressao,
                viewData.valrClasResiTracaoDireta,
                viewData.valrClasResiTracaoFlexao,
                viewData.valrClasResiTracaoIndireta,
                viewData.valrClasTrabalhabilidade,
              ].some((v) => v !== null && v !== undefined && String(v).trim() !== '') ? (
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Classificação de resistência</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      { label: 'Resistência à compressão', value: viewData.valrClasResiCompressao },
                      { label: 'Resistência à tração direta', value: viewData.valrClasResiTracaoDireta },
                      { label: 'Resistência à tração na flexão', value: viewData.valrClasResiTracaoFlexao },
                      { label: 'Resistência à tração indireta', value: viewData.valrClasResiTracaoIndireta },
                      { label: 'Trabalhabilidade', value: viewData.valrClasTrabalhabilidade },
                    ].filter(({ value }) => value !== null && value !== undefined && String(value).trim() !== '').map(({ label, value }) => (
                      <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                        <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
                        <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Sinalizações */}
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Sinalizações</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { label: 'Adição de água', value: ADICAO_AGUA_LABELS[String(viewData.flagContAdicaoAgua)] || ADICAO_AGUA_LABELS['0'] },
                    { label: 'IEV', value: String(viewData.flagIev) === '1' ? 'Sim' : 'Não' },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
                      <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Horarios */}
              {(viewData.dataSaidaUsina || viewData.dataChegadaUsina || viewData.dataChegadaObra || viewData.dataInicioDescarga || viewData.dataFimDescarga) ? (
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Horarios</p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[
                      { label: 'Saída da usina', value: formatHour(viewData.dataSaidaUsina) },
                      { label: 'Chegada na usina', value: formatHour(viewData.dataChegadaUsina) },
                      { label: 'Chegada na obra', value: formatHour(viewData.dataChegadaObra) },
                      { label: 'Início da descarga', value: formatHour(viewData.dataInicioDescarga) },
                      { label: 'Fim da descarga', value: formatHour(viewData.dataFimDescarga) },
                    ].filter(({ value }) => value && value !== '--:--').map(({ label, value }) => (
                      <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                        <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
                        <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Temperaturas */}
              {(viewData.valrTemperaturaConcreto || viewData.valrTemperaturaAmbiente) ? (
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Temperaturas</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      { label: 'Temperatura do concreto (°C)', value: viewData.valrTemperaturaConcreto ?? '-' },
                      { label: 'Temperatura ambiente (°C)', value: viewData.valrTemperaturaAmbiente ?? '-' },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                        <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
                        <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Corpos de prova */}
              {Array.isArray(viewData.corpoProvas) && viewData.corpoProvas.length > 0 ? (
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Corpos de prova ({viewData.corpoProvas.length})</p>
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        <tr>
                          <th className="px-3 py-2">Etiqueta</th>
                          <th className="px-3 py-2">Tipo</th>
                          <th className="px-3 py-2">Idade (dias)</th>
                          <th className="px-3 py-2">Data de ruptura</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewData.corpoProvas.map((cp, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium text-slate-800">{cp.idenObraEtiqueta || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{resolveViewLabel(tipoCorpoProvaOptions, cp.idTipoCorpoProva, cp.tipoCorpoProva, ['nomeTipoCorpoProva', 'nome_tipo_corpo_prova', 'sigla', 'descricao', 'nome'])}</td>
                            <td className="px-3 py-2 text-slate-600">{cp.idadeRuptura ?? '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{formatDateBr(normalizeDateOnly(cp.dataRuptura || ''))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Testes slump */}
              {Array.isArray(viewData.testeSlumps) && viewData.testeSlumps.length > 0 ? (
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Testes slump ({viewData.testeSlumps.length})</p>
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        <tr>
                          <th className="px-3 py-2">Adição de água (L)</th>
                          <th className="px-3 py-2">Adição de aditivo (L)</th>
                          <th className="px-3 py-2">Valor slump (mm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewData.testeSlumps.map((ts, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-600">{ts.valrAdicaoAgua ?? '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{ts.valrAdicaoAditivo ?? '-'}</td>
                            <td className="px-3 py-2 font-medium text-slate-800">{ts.valrSlump ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Observações */}
              {viewData.observacao ? (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Observações</p>
                  <p className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">{viewData.observacao}</p>
                </div>
              ) : null}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setViewData(null)}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isWizardOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeWizard}
            aria-label="Fechar modal"
          />
          <form
            className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-y-auto space-y-6 rounded-2xl bg-white p-4 shadow-xl sm:p-6"
            onSubmit={handleSubmit}
          >
            <div className="sticky top-0 z-20 -mx-4 -mt-4 flex items-start justify-between gap-3 border-b border-slate-100 bg-white px-4 pb-3 pt-4 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">Cadastrar caminhão</h3>
                <p className="mt-1 text-sm text-slate-500">Preencha os blocos por etapa como no fluxo atual.</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {selectedConcretagem ? (
                  <div className="rounded-lg border border-[#0e7490]/20 bg-[#0e7490]/10 px-3 py-2 text-right">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0e7490]">Concretagem em cadastro</p>
                    <p className="mt-0.5 max-w-[260px] text-xs text-slate-700">
                      {selectedConcretagem.obra} | {formatDateBr(selectedConcretagem.data)} | {formatHour(selectedConcretagem.dataHoraConcretagem)}
                    </p>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={closeWizard}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {saveMessage ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {saveMessage}
              </div>
            ) : null}

            {currentStep >= 2 ? (
              <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e7490]/10 text-sm font-semibold text-[#0e7490]">
                    {currentStep > 2 ? <Check className="h-4 w-4" /> : '2'}
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Características de classificação</h2>
                    <p className="text-sm text-slate-500">Metadados e FKs da classificação.</p>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Etapa da obra <span className="text-rose-500">*</span></label>
                    <select
                      value={formData.etapaObra}
                      onChange={handleFieldChange('etapaObra')}
                      disabled={loadingClassificacao}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <option value="">{loadingClassificacao ? 'Carregando...' : 'Selecione'}</option>
                      {etapasObraOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    {errors.etapaObra ? <p className="text-xs text-rose-600">{errors.etapaObra}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Tipo de cimento <span className="text-rose-500">*</span></label>
                    <select
                      value={formData.tipoCimento}
                      onChange={handleFieldChange('tipoCimento')}
                      disabled={loadingClassificacao}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <option value="">{loadingClassificacao ? 'Carregando...' : 'Selecione'}</option>
                      {tipoCimentoOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    {errors.tipoCimento ? <p className="text-xs text-rose-600">{errors.tipoCimento}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Tipo de concreto <span className="text-rose-500">*</span></label>
                    <select
                      value={formData.tipoConcreto}
                      onChange={handleFieldChange('tipoConcreto')}
                      disabled={loadingClassificacao}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <option value="">{loadingClassificacao ? 'Carregando...' : 'Selecione'}</option>
                      {tipoConcretoOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    {errors.tipoConcreto ? <p className="text-xs text-rose-600">{errors.tipoConcreto}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Concreteira <span className="text-rose-500">*</span></label>
                    <select
                      value={formData.concreteira}
                      onChange={handleFieldChange('concreteira')}
                      disabled={loadingClassificacao}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <option value="">{loadingClassificacao ? 'Carregando...' : 'Selecione'}</option>
                      {concreteiraOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    {errors.concreteira ? <p className="text-xs text-rose-600">{errors.concreteira}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Carta traço:</label>
                    <select
                      value={formData.cartaTraco}
                      onChange={handleFieldChange('cartaTraco')}
                      disabled={loadingClassificacao}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <option value="">{loadingClassificacao ? 'Carregando...' : 'Selecione'}</option>
                      {cartaTracoOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {currentStep === 2 ? (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAdvanceFromStep2}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#0e7490] hover:text-[#0e7490]"
                    >
                      Avançar
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </section>
            ) : null}

            {currentStep >= 3 ? (
              <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e7490]/10 text-sm font-semibold text-[#0e7490]">
                    {currentStep > 3 ? <Check className="h-4 w-4" /> : '3'}
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Caracteristicas técnicas e documentais</h2>
                    <p className="text-sm text-slate-500">Dados técnicos e nota fiscal.</p>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Placa do caminhão:</label>
                    <input
                      type="text"
                      value={formData.placaCaminhao}
                      onChange={handleFieldChange('placaCaminhao')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">NF / Remessa / Romaneio - Principal:</label>
                    <input
                      type="number"
                      value={formData.numrNotaFiscalPrincipal}
                      onChange={handleFieldChange('numrNotaFiscalPrincipal')}
                      disabled={isSemNotaFiscal}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={isSemNotaFiscal}
                        onChange={handleSemNotaFiscalChange}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      Sem nota fiscal para este cadastro
                    </label>
                    {errors.notaFiscalRegra ? (
                      <p className="text-xs text-rose-600">{errors.notaFiscalRegra}</p>
                    ) : null}
                    {errors.numrNotaFiscalPrincipal ? (
                      <p className="text-xs text-rose-600">{errors.numrNotaFiscalPrincipal}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">NF / Remessa / Romaneio - Secundário (Se houver):</label>
                    <input
                      type="number"
                      value={formData.numrNotaFiscalSecundaria}
                      onChange={handleFieldChange('numrNotaFiscalSecundaria')}
                      disabled={isSemNotaFiscal}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {errors.numrNotaFiscalSecundaria ? (
                      <p className="text-xs text-rose-600">{errors.numrNotaFiscalSecundaria}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Número do lacre do caminhão:</label>
                    <input
                      type="text"
                      value={formData.numrLacreCaminhao}
                      onChange={handleFieldChange('numrLacreCaminhao')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {errors.numrLacreCaminhao ? (
                      <p className="text-xs text-rose-600">{errors.numrLacreCaminhao}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Volume (m³):</label>
                    <input
                      type="number"
                      value={formData.valrVolume}
                      onChange={handleFieldChange('valrVolume')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Local de concretagem:</label>
                    <input
                      type="text"
                      value={formData.localConcretagem}
                      onChange={handleFieldChange('localConcretagem')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Classe de resistência à compressão (FCK) (MPa):</label>
                    <input
                      type="number"
                      value={formData.valrClasResiCompressao}
                      onChange={handleFieldChange('valrClasResiCompressao')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Resistência à tração direta (MPa):</label>
                    <input
                      type="number"
                      value={formData.valrClasResiTracaoDireta}
                      onChange={handleFieldChange('valrClasResiTracaoDireta')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Resistência tração flexão (MPa):</label>
                    <input
                      type="number"
                      value={formData.valrClasResiTracaoFlexao}
                      onChange={handleFieldChange('valrClasResiTracaoFlexao')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Resistência tração indireta (MPa):</label>
                    <input
                      type="number"
                      value={formData.valrClasResiTracaoIndireta}
                      onChange={handleFieldChange('valrClasResiTracaoIndireta')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Classe de trabalhabilidade:</label>
                    <input
                      type="number"
                      value={formData.valrClasTrabalhabiilidade}
                      onChange={handleFieldChange('valrClasTrabalhabiilidade')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                </div>
                {currentStep === 3 ? (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAdvanceFromStep3}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#0e7490] hover:text-[#0e7490]"
                    >
                      Avançar
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </section>
            ) : null}

            {currentStep >= 4 ? (
              <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e7490]/10 text-sm font-semibold text-[#0e7490]">
                    {currentStep > 4 ? <Check className="h-4 w-4" /> : '4'}
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Controle e flags</h2>
                    <p className="text-sm text-slate-500">Indicadores de adicao e verificacao.</p>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Adição de água</label>
                    <select
                      value={formData.flagContAdicaoAgua}
                      onChange={handleFieldChange('flagContAdicaoAgua')}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 ${errors.flagContAdicaoAgua ? 'border-rose-400' : 'border-slate-200'}`}
                    >
                      {Object.entries(ADICAO_AGUA_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    {errors.flagContAdicaoAgua ? (
                      <p className="text-xs text-rose-600">{errors.flagContAdicaoAgua}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">IEV (Índice de Estabilidade Visual)</label>
                    <select
                      value={formData.flagIev}
                      onChange={handleFieldChange('flagIev')}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 ${errors.flagIev ? 'border-rose-400' : 'border-slate-200'}`}
                    >
                      <option value="-1">Selecione</option>
                      <option value="0">Não aplicado</option>
                      <option value="1">0</option>
                      <option value="2">1</option>
                      <option value="3">2</option>
                      <option value="4">3</option>
                    </select>
                    {errors.flagIev ? (
                      <p className="text-xs text-rose-600">{errors.flagIev}</p>
                    ) : null}
                  </div>
                </div>
                {currentStep === 4 ? (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAdvanceFromStep4}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#0e7490] hover:text-[#0e7490]"
                    >
                      Avançar
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </section>
            ) : null}

            {currentStep >= 5 ? (
              <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e7490]/10 text-sm font-semibold text-[#0e7490]">
                    {currentStep > 5 ? <Check className="h-4 w-4" /> : '5'}
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Condições de descarga</h2>
                    <p className="text-sm text-slate-500">Informe apenas as horas. A data usada será a da concretagem realizada.</p>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Hora saída usina</label>
                    <input
                      type="time"
                      value={formData.dataSaidaUsina}
                      onChange={handleFieldChange('dataSaidaUsina')}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 ${errors.dataSaidaUsina ? 'border-rose-400' : 'border-slate-200'}`}
                    />
                    {errors.dataSaidaUsina ? (
                      <p className="text-xs text-rose-600">{errors.dataSaidaUsina}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Hora chegada obra</label>
                    <input
                      type="time"
                      value={formData.dataChegadaObra}
                      onChange={handleFieldChange('dataChegadaObra')}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 ${errors.dataChegadaObra ? 'border-rose-400' : 'border-slate-200'}`}
                    />
                    {errors.dataChegadaObra ? (
                      <p className="text-xs text-rose-600">{errors.dataChegadaObra}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Hora início descarga</label>
                    <input
                      type="time"
                      value={formData.dataInicioDescarga}
                      onChange={handleFieldChange('dataInicioDescarga')}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 ${errors.dataInicioDescarga ? 'border-rose-400' : 'border-slate-200'}`}
                    />
                    {errors.dataInicioDescarga ? (
                      <p className="text-xs text-rose-600">{errors.dataInicioDescarga}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Hora fim descarga</label>
                    <input
                      type="time"
                      value={formData.dataFimDescarga}
                      onChange={handleFieldChange('dataFimDescarga')}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 ${errors.dataFimDescarga ? 'border-rose-400' : 'border-slate-200'}`}
                    />
                    {errors.dataFimDescarga ? (
                      <p className="text-xs text-rose-600">{errors.dataFimDescarga}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Hora chegada usina</label>
                    <input
                      type="time"
                      value={formData.dataChegadaUsina}
                      onChange={handleFieldChange('dataChegadaUsina')}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 ${errors.dataChegadaUsina ? 'border-rose-400' : 'border-slate-200'}`}
                    />
                    {errors.dataChegadaUsina ? (
                      <p className="text-xs text-rose-600">{errors.dataChegadaUsina}</p>
                    ) : null}
                  </div>
                  <div className="hidden lg:block" aria-hidden="true" />
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Temp. concreto (C)</label>
                    <input
                      type="number"
                      value={formData.valrTemperaturaConcreto}
                      onChange={handleFieldChange('valrTemperaturaConcreto')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Temp. ambiente (C)</label>
                    <input
                      type="number"
                      value={formData.valrTemperaturaAmbiente}
                      onChange={handleFieldChange('valrTemperaturaAmbiente')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                </div>
                {currentStep === 5 ? (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAdvanceFromStep5}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#0e7490] hover:text-[#0e7490]"
                    >
                      Avançar
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </section>
            ) : null}

            {currentStep >= 6 ? (
              <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e7490]/10 text-sm font-semibold text-[#0e7490]">
                    {currentStep > 6 ? <Check className="h-4 w-4" /> : '6'}
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Teste de slump</h2>
                    <p className="text-sm text-slate-500">A data segue a concretagem e o slump final e calculado pelo ultimo slump informado.</p>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="text-sm font-medium text-slate-700">Quantidade de testes de slump <span className="text-rose-500">*</span></label>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleAdjustQuantidadeSlump('decrement')}
                        disabled={isMinusSlumpDisabled}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Diminuir quantidade de testes de slump"
                      >
                        -
                      </button>
                      <div className="w-44 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-sm font-semibold text-slate-700">
                        {Number(formData.quantidadeSlump) > 0
                          ? `${formData.quantidadeSlump} teste${Number(formData.quantidadeSlump) > 1 ? 's' : ''}`
                          : 'Selecione com +'}
                      </div>
                      <button
                        type="button"
                        onClick={handleAdjustQuantidadeSlump('increment')}
                        disabled={isPlusSlumpDisabled}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Aumentar quantidade de testes de slump"
                      >
                        +
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      {[1, 2, 3].map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={handleAddQuantidadeSlump(amount)}
                          disabled={isPlusSlumpDisabled}
                          className="inline-flex h-10 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          +{amount} teste{amount > 1 ? 's' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                  {errors.quantidadeSlump ? (
                    <p className="mt-2 text-xs text-rose-600">{errors.quantidadeSlump}</p>
                  ) : null}
                </div>

                {slumpRows.length === 0 ? (
                  <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    Defina a quantidade para cadastrar os testes de slump.
                  </div>
                ) : (
                  <div className="mt-5 space-y-4">
                    {slumpRows.map((row, index) => (
                      <div key={`slump-${index}`} className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="text-sm font-semibold text-slate-700">
                          {index === slumpRows.length - 1 ? 'Teste slump de descarga' : `Teste slump ${index + 1}`}
                        </div>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Adição de água (L) <span className="text-rose-500">*</span></label>
                            <input
                              type="number"
                              value={row.valrAdicaoAgua}
                              onChange={handleSlumpChange(index, 'valrAdicaoAgua')}
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                            />
                            {errors[getSlumpErrorKey(index, 'valrAdicaoAgua')] ? (
                              <p className="text-xs text-rose-600">{errors[getSlumpErrorKey(index, 'valrAdicaoAgua')]}</p>
                            ) : null}
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Adição de aditivo (L) <span className="text-rose-500">*</span></label>
                            <input
                              type="number"
                              value={row.valrAdicaoAditivo}
                              onChange={handleSlumpChange(index, 'valrAdicaoAditivo')}
                              placeholder="Ex: 2"
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                            />
                            {errors[getSlumpErrorKey(index, 'valrAdicaoAditivo')] ? (
                              <p className="text-xs text-rose-600">{errors[getSlumpErrorKey(index, 'valrAdicaoAditivo')]}</p>
                            ) : null}
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Valor slump (mm) <span className="text-rose-500">*</span></label>
                            <input
                              type="number"
                              value={row.valrSlump}
                              onChange={handleSlumpChange(index, 'valrSlump')}
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                            />
                            {errors[getSlumpErrorKey(index, 'valrSlump')] ? (
                              <p className="text-xs text-rose-600">{errors[getSlumpErrorKey(index, 'valrSlump')]}</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {currentStep === 6 ? (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAdvanceFromStep6}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#0e7490] hover:text-[#0e7490]"
                    >
                      Avançar
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </section>
            ) : null}

            {currentStep >= 7 ? (
              <>
                <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e7490]/10 text-sm font-semibold text-[#0e7490]">
                      {currentStep > 7 ? <Check className="h-4 w-4" /> : '7'}
                    </span>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">Quantidade de CP e corpos de prova</h2>
                      <p className="text-sm text-slate-500">Defina a quantidade e preencha os dados obrigatórios de cada CP neste mesmo bloco.</p>
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="text-sm font-medium text-slate-700">Quantidade de CP <span className="text-rose-500">*</span></label>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleAdjustQuantidadeCp('decrement')}
                          disabled={isMinusDisabled}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Diminuir quantidade de CP"
                        >
                          -
                        </button>
                        <div className="w-44 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-sm font-semibold text-slate-700">
                          {Number(formData.quantidadeCp) > 0
                            ? `${formData.quantidadeCp} CP${Number(formData.quantidadeCp) > 1 ? 's' : ''}`
                            : 'Selecione com +'}
                        </div>
                        <button
                          type="button"
                          onClick={handleAdjustQuantidadeCp('increment')}
                          disabled={isPlusDisabled}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Aumentar quantidade de CP"
                        >
                          +
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {[5, 10, 15].map((amount) => (
                          <button
                            key={amount}
                            type="button"
                            onClick={handleAddQuantidadeCp(amount)}
                            disabled={isPlusDisabled}
                            className="inline-flex h-10 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490] disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            +{amount} CPs
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Dica: use os botões para ajustar sem perder os CPs preenchidos.
                    </p>
                    {errors.quantidadeCp ? (
                      <p className="text-xs text-rose-600">{errors.quantidadeCp}</p>
                    ) : null}
                  </div>

                  {cpRows.length === 0 ? (
                    <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                      Informe a quantidade de CP para liberar os campos abaixo.
                    </div>
                  ) : (
                    <div className="mt-5">
                      {/* Cabeçalho das colunas (apenas em telas largas) */}
                      <div className="mb-2 hidden gap-3 px-1 lg:grid lg:grid-cols-[70px_1fr_1fr_1fr_1fr]">
                        <span />
                        <span className="text-sm font-medium text-slate-700">Idade de ruptura (dias) <span className="text-rose-500">*</span></span>
                        <span className="text-sm font-medium text-slate-700">Etiqueta <span className="text-rose-500">*</span></span>
                        <span className="text-sm font-medium text-slate-700">Tipo <span className="text-rose-500">*</span></span>
                        <span className="text-sm font-medium text-slate-700">Data de ruptura</span>
                      </div>

                      <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
                        {cpRows.map((row, index) => (
                          <div key={index} className="grid items-start gap-3 p-3 lg:grid-cols-[70px_1fr_1fr_1fr_1fr]">
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 lg:pt-2">
                              <ClipboardCheck className="h-4 w-4" />
                              CP {index + 1}
                            </div>

                            <div className="space-y-1">
                              <label className="text-xs font-medium text-slate-600 lg:hidden">Idade de ruptura (dias) <span className="text-rose-500">*</span></label>
                              <input
                                type="number"
                                value={row.idadeRuptura}
                                onChange={handleCpChange(index, 'idadeRuptura')}
                                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                              />
                              {errors[getCpErrorKey(index, 'idadeRuptura')] ? (
                                <p className="text-xs text-rose-600">{errors[getCpErrorKey(index, 'idadeRuptura')]}</p>
                              ) : null}
                            </div>

                            <div className="space-y-1">
                              <label className="text-xs font-medium text-slate-600 lg:hidden">Etiqueta <span className="text-rose-500">*</span></label>
                              <div className="relative">
                                <input
                                  ref={(el) => (cpEtiquetaRefs.current[index] = el)}
                                  type="text"
                                  value={row.etiqueta}
                                  onChange={handleCpChange(index, 'etiqueta')}
                                  onKeyDown={(e) => handleEtiquetaKeyDown(e, index)}
                                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 relative"
                                />
                                {(!row.etiqueta || String(row.etiqueta).trim() === '') && cpSuggestions[index] ? (
                                  <div className="pointer-events-none absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-slate-400">
                                    {cpSuggestions[index]}
                                  </div>
                                ) : null}
                              </div>
                              {errors[getCpErrorKey(index, 'etiqueta')] ? (
                                <p className="text-xs text-rose-600">{errors[getCpErrorKey(index, 'etiqueta')]}</p>
                              ) : null}
                            </div>

                            <div className="space-y-1">
                              <label className="text-xs font-medium text-slate-600 lg:hidden">Tipo <span className="text-rose-500">*</span></label>
                              <select
                                value={row.tipo}
                                onChange={handleCpChange(index, 'tipo')}
                                disabled={loadingClassificacao || (aplicarTipoTodosCp && index !== 0)}
                                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <option value="">
                                  {loadingClassificacao
                                    ? 'Carregando...'
                                    : tipoCorpoProvaOptions.length === 0
                                      ? 'Nenhum tipo cadastrado'
                                      : 'Selecione'}
                                </option>
                                {tipoCorpoProvaOptions.map((option) => (
                                  <option key={option.id} value={option.id}>{option.label}</option>
                                ))}
                              </select>
                              {errors[getCpErrorKey(index, 'tipo')] ? (
                                <p className="text-xs text-rose-600">{errors[getCpErrorKey(index, 'tipo')]}</p>
                              ) : null}
                              {index === 0 ? (
                                <label className="flex items-center gap-2 text-xs text-slate-600">
                                  <input
                                    type="checkbox"
                                    checked={aplicarTipoTodosCp}
                                    onChange={handleToggleAplicarTipoTodos}
                                    className="h-3.5 w-3.5 rounded border-slate-300 text-[#0e7490] focus:ring-[#0e7490]"
                                  />
                                  Aplicar para todos os CPs
                                </label>
                              ) : null}
                            </div>

                            <div className="space-y-1">
                              <label className="text-xs font-medium text-slate-600 lg:hidden">Data de ruptura</label>
                              <input
                                type="date"
                                value={calculateDataRuptura(formData.dataConcretagem, row.idadeRuptura)}
                                readOnly
                                className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>

                <section className="step-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900">Observações gerais do caminhão</h2>
                  <p className="mt-1 text-sm text-slate-500">Registre aqui observacoes gerais apos preencher os corpos de prova.</p>

                  <div className="mt-4 space-y-2">
                    <label className="text-sm font-medium text-slate-700">Observações</label>
                    <textarea
                      rows={3}
                      value={formData.observacoes}
                      onChange={handleFieldChange('observacoes')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                </section>

                <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-500">Revise os dados antes de salvar.</div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                    <button
                      type="button"
                      onClick={handleConfirmResetWizard}
                      className="rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                    >
                      Limpar
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0e7490] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0e7490]/90 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isSaving ? 'Salvando...' : 'Salvar cadastro'}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </section>
              </>
            ) : null}

          </form>
        </div>
      ) : null}
    </div>
  );
}

export default CaminhoesPage;


